import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

const triggerGuidance = {
  task: "Assign work to someone who needs to complete a form, review information or approve a decision. Use a task when you need to track an action through to completion.",
  notification:
    "Show an alert in the recipient’s application, such as a status update, reminder or warning. Use a notification for updates within the application; use a communication to send an email or SMS.",
  comms:
    "Send an email or SMS to recipients, for example to confirm an outcome or request information. Use a communication to reach people through those channels; use a notification for an alert within the application.",
  integration:
    "Connect the decision flow to another system through an API. Use an integration to retrieve information, send results or ask another system to perform an action.",
  storage:
    "Save data from the decision flow as a file in a storage container. Use storage to export results for reporting, record keeping or later processing.",
  contact:
    "Update information on an existing contact using data from the decision flow. Use a contact trigger to keep a person’s contact record up to date.",
  entity:
    "Create or update an entity record using data from the decision flow. Use an entity trigger to save information about a business object, such as a request or organisation, for reuse.",
};

interface TriggerIntroductionProps {
  type: keyof typeof triggerGuidance;
}

/** Displays the trigger type and explains when to use it above its settings. */
export const TriggerIntroduction = ({ type }: TriggerIntroductionProps): ReactElement => {
  const { t } = useTranslation();
  const title = type === "notification" ? "Notification" : t(`flow.${type === "comms" ? "communication" : type}`);

  return (
    <div className="flex flex-col gap-2">
      <h2 className="m-0 text-xl font-semibold text-foreground">{title}</h2>
      <p className="m-0 text-sm text-muted-foreground">{triggerGuidance[type]}</p>
    </div>
  );
};

/** Renders a smaller subsection heading beneath the trigger introduction. */
export const TriggerSectionTitle = ({ children }: { children: ReactNode }): ReactElement => (
  <h3 className="m-0 text-base font-semibold text-foreground">{children}</h3>
);
